<template>
  <div
    id="div-mapa"
    :class="{
      'animate-bottom': config.open,
      'animate-bottom-reverse': !config.open,
    }"
    v-show="config.open !== false"
  >
    <el-row class="header-map" v-if="config.showHeader">
      <el-col :span="20">
        <span class="title-map">Visualizar no mapa</span>
      </el-col>
      <el-col :span="4">
        <el-button
          class="close-map"
          :style="`color: ${estilo.aplicacao.corPrimariaAplicacao}`"
          @click="closeMap"
          type="text"
        >
          Fechar
        </el-button>
      </el-col>
    </el-row>

    <!-- 1) EMBED grátis quando NÃO precisa de interatividade e há exatamente 1 ponto -->
    <div
      v-if="useEmbed"
      class="map-embed-wrapper"
      :style="`height: ${safeHeight}`"
    >
      <iframe
        :src="embedSrc"
        style="border: 0; width: 100%; height: 100%; border-radius: 10px"
        loading="lazy"
        referrerpolicy="no-referrer-when-downgrade"
        allowfullscreen
      ></iframe>
    </div>

    <!-- 2) JS (billable) somente quando precisa de interatividade / múltiplos pontos -->
    <GoogleMap
      v-else
      :apikey="aplicacao.googleMapsApiKeyGeocodeLocation"
      :config="mapOptions"
      :lazy="true"
    >
      <GoogleMapMarkers
        v-for="(item, index) in locations"
        ref="markers"
        :key="index"
        :markers="[].concat(item.coordenadas)"
        :refObject="item.refObject"
        :user-location="userloc"
        :pin-color="pinColor"
        :active-coord="activeCoord"
        @marker-click="processMarkerClick"
      />
    </GoogleMap>

    <div class="slot-content">
      <div><slot /></div>
    </div>
  </div>
</template>

<script>
import GoogleMap from './GoogleMap'
import GoogleMapMarkers from './GoogleMapMarkers'
import { mapGetters } from 'vuex'

export default {
  name: 'Mapa',
  components: { GoogleMap, GoogleMapMarkers },
  props: {
    locations: { type: Array, required: true },
    config: {
      type: Object,
      default: () => ({
        showHeader: true,
        showCardInfo: true,
        showActions: true,
        height: '200px',
        zoom: 10,
        center: {},
        gestureHandling: 'greedy',
        fullscreenControl: true,
        open: true,
        // quando false e houver exatamente 1 ponto, usamos <iframe> (grátis)
        interactive: true
      })
    },
    onMarkerClick: Function,
    pinColor: { default: '#016fb9' }
  },
  data () {
    return {
      copiado: false,
      userloc: null,
      activeIndex: 0 // índice do ponto ativo
    }
  },
  computed: {
    ...mapGetters('SetupApp', ['estilo', 'aplicacao']),

    colorButton () {
      return `background: ${this.estilo.aplicacao.corPrimariaAplicacao}`
    },
    colorBorder () {
      return `border: 1px solid ${this.estilo.aplicacao.corPrimariaAplicacao}; color: ${this.estilo.aplicacao.corPrimariaAplicacao}`
    },

    // todas as coordenadas achatadas
    allCoords () {
      return this._flattenCoords()
    },
    hasLocation () {
      return Array.isArray(this.allCoords) && this.allCoords.length > 0
    },

    // embed somente quando não interativo e houver 1 ponto
    useEmbed () {
      if (this.config.interactive) return false
      return this.allCoords.length === 1
    },

    // URL do embed a partir do único ponto
    embedSrc () {
      const c = this.allCoords[0]
      const q = `${c.lat},${c.long}`
      return `https://www.google.com/maps?q=${encodeURIComponent(
        q
      )}&output=embed&hl=pt-BR&z=${this.config.zoom || 10}`
    },

    // opções do mapa JS
    mapOptions () {
      const c = this.allCoords[0]
      return {
        disableDefaultUI: true,
        gestureHandling: this.config.gestureHandling || 'greedy',
        fullscreenControl: !!this.config.fullscreenControl,
        zoom: this.config.zoom || 10,
        center: c
          ? {
            lat: Number(String(c.lat).replace(',', '.')),
            lng: Number(String(c.long).replace(',', '.'))
          }
          : { lat: -14.235, lng: -51.925 },
        height: this.safeHeight
      }
    },

    // coord ativa (para pan/zoom ao trocar)
    activeCoord () {
      return this.allCoords[this.activeIndex] || null
    },

    // altura
    safeHeight () {
      return this.config.height || '200px'
    }
  },
  watch: {
    // se mudar locations, recomeça do primeiro ponto válido
    locations: {
      deep: true,
      handler () {
        if (!this.hasLocation) {
          this.activeIndex = 0
          return
        }
        // mantém índice dentro do range
        if (this.activeIndex >= this.allCoords.length) {
          this.activeIndex = 0
        }
      }
    }
  },
  methods: {
    closeMap () {
      this.config.open = false
      setTimeout(() => this.$emit('close'), 400)
    },

    // trocar para o ponto i (use isso no seu UI/menu/lista)
    focusPoint (i) {
      if (!this.hasLocation) return
      const idx = Number(i)
      if (Number.isNaN(idx)) return
      this.activeIndex = Math.max(0, Math.min(idx, this.allCoords.length - 1))
      // os GoogleMapMarkers vão observar activeCoord e dar o pan/zoom
    },

    copyText () {
      const el = document.querySelector('.text-card-map')
      const copyTextarea = el ? el.innerHTML : ''
      const text = document.createElement('input')
      document.body.appendChild(text)
      text.value = copyTextarea
      text.select()
      document.execCommand('copy', false)
      text.remove()
      this.copiado = true
      setTimeout(() => {
        this.copiado = false
      }, 500)
    },

    // compat: panTo usando refs (continua funcionando)
    panToPosition (lat, lon) {
      this.$refs.markers.some((x) => x.panToPosition(lat, lon))
    },

    // abre GMaps no ponto ativo
    openGmap () {
      const c = this.activeCoord || this.allCoords[0]
      if (!c) return
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${c.lat},${c.long}`,
        '_blank'
      )
    },

    processMarkerClick (googleMarker) {
      if (typeof this.onMarkerClick === 'function') {
        this.onMarkerClick(googleMarker)
      }
    },

    // utils
    _flattenCoords () {
      const out = []
      if (!Array.isArray(this.locations)) return out

      for (const item of this.locations) {
        const coords = item.coordenadas
        if (!coords) continue

        if (Array.isArray(coords)) {
          for (const c of coords) {
            const lat = this._toNum(c.lat)
            const lng = this._toNum(c.long)
            if (this._isValid(lat, lng)) { out.push({ lat, long: lng, name: c.name }) }
          }
        } else {
          const lat = this._toNum(coords.lat)
          const lng = this._toNum(coords.long)
          if (this._isValid(lat, lng)) { out.push({ lat, long: lng, name: coords.name }) }
        }
      }
      return out
    },
    _toNum (v) {
      if (typeof v === 'string') return Number(v.replace(',', '.'))
      return typeof v === 'number' ? v : NaN
    },
    _isValid (lat, lng) {
      return Number.isFinite(lat) && Number.isFinite(lng)
    }
  },
  created () {
    // Geolocalização do navegador (grátis)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const latitude = parseFloat(pos.coords.latitude)
        const longitude = parseFloat(pos.coords.longitude)
        this.userloc = { lat: latitude, lng: longitude }
      },
      () => {
        this.userloc = null
      },
      { maximumAge: 60000, timeout: 8000, enableHighAccuracy: false }
    )
  }
}
</script>

<style scoped>
@keyframes animatebottom {
  from {
    bottom: -400px;
    opacity: 0;
  }
  to {
    bottom: 10px;
    opacity: 1;
  }
}
@keyframes animateclose {
  to {
    bottom: -400px;
    opacity: 0;
  }
  from {
    bottom: 10px;
    opacity: 1;
  }
}
.animate-bottom {
  position: relative;
  animation: animatebottom 0.4s;
}
.animate-bottom-reverse {
  position: relative;
  animation: animateclose 0.4s;
}

.mapa {
  left: 15px;
  bottom: 10px;
  width: 119%;
  background-color: white;
}
.header-map {
  padding-bottom: 20px;
}

.title-map {
  font-size: 20px;
  text-align: left;
  font-family: "Open Sans";
  font-weight: 600;
  color: #333333;
  line-height: 27px;
}
.close-map {
  top: 5px;
  left: 10px;
  position: relative;
  float: right;
}

.card-map {
  position: relative;
  bottom: 15px;
  z-index: 20000;
  width: 400px;
  margin-left: auto;
  margin-right: auto;
  border-radius: 6px;
  max-height: 187px;
}
.title-card-map {
  font-size: 16px;
  text-align: left;
  line-height: 22px;
  color: #333333;
  margin-bottom: 5px;
  display: block;
}
.text-card-map {
  color: #555555;
  text-align: left;
  line-height: 15px;
  font-size: 12px;
}
.buttons-card-map {
  margin-top: 15px;
}
.button-copy {
  margin-left: 27px;
  height: 29px;
}
.button-gmap {
  height: 29px;
}

.copiado {
  background: #0000009f;
  position: relative;
  bottom: 185px;
  width: 400px;
  margin-left: auto;
  margin-right: auto;
}
.copiado-alerta {
  color: #ffffff;
  display: flex;
  justify-content: center;
}

.slot-content {
  position: absolute;
  width: 100%;
  bottom: 0px;
}

.map-embed-wrapper {
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
}
</style>
